import { useState } from "react";
import { useAccess } from "../../../access/hooks/useAccess";
import {
  Card,
  Plus,
  Trash2,
  Save,
  CircleX,
  Select,
  Input,
  Button,
} from "../../ui";
import { getLocalized } from "../utils/notificationHelpers";
import { DeleteModal } from "../../modals/Delete";
import {
  createNotificationTemplateAPI,
  updateNotificationTemplateAPI,
  deleteNotificationTemplateAPI,
} from "../../../utils/api/notifications";
import { reportApiError, reportFailedBody } from "../../../utils/errors/presentError";

const BUILDER_CATS = ["menu", "manager", "report", "ticket"];

const initialForm = (category = "menu") => ({
  category,
  nameAr: "",
  nameEn: "",
  bodyAr: "",
  bodyEn: "",
  needsDetail: false,
  placeholderAr: "",
  placeholderEn: "",
  detailHint: "",
});

const TEMPLATE_LABEL_EN = {
  menu: "Menu",
  manager: "Account Manager",
  report: "Reports",
  ticket: "Tickets",
};

const getTemplateId = (template) => template?._id || template?.id || "";

const getToken = () => localStorage.getItem("auth-token");

export default function TemplateBuilder({
  templates,
  lang,
  t,
  toastSaved,
  onTemplatesChange,
}) {
  const { can } = useAccess();
  const [builderCat, setBuilderCat] = useState("menu");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm("menu"));
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteState, setDeleteState] = useState({ loading: false, error: "" });
  const [saving, setSaving] = useState(false);
  const ar = lang === "ar";

  const selectedList = templates[builderCat] || [];
  const currentCategoryLabel = t.categories[form.category];

  const resetForm = (category = builderCat) => {
    setEditingId(null);
    setForm(initialForm(category));
  };

  const handleCategory = (category) => {
    setBuilderCat(category);
    resetForm(category);
  };

  const editTemplate = (template) => {
    const templateId = getTemplateId(template);

    setEditingId(templateId);
    setForm({
      category: template.category || template.cat || builderCat,
      nameAr: template.nameAr || "",
      nameEn: template.nameEn || "",
      bodyAr: template.bodyAr || "",
      bodyEn: template.bodyEn || "",
      needsDetail: Boolean(template.needsDetail),
      placeholderAr: template.placeholderAr || "",
      placeholderEn: template.placeholderEn || "",
      detailHint: template.detailHint || "",
    });
  };

  const getPayload = () => ({
    category: form.category,
    nameAr: form.nameAr.trim(),
    nameEn: form.nameEn.trim(),
    bodyAr: form.bodyAr.trim(),
    bodyEn: form.bodyEn.trim(),
    needsDetail: form.needsDetail,
    placeholderAr: form.needsDetail ? form.placeholderAr : "",
    placeholderEn: form.needsDetail ? form.placeholderEn : "",
    detailHint: form.needsDetail ? form.detailHint : "",
    tag: form.category,
    lbl: t.categories[form.category],
    lblEn: TEMPLATE_LABEL_EN[form.category],
  });

  const saveTemplate = async () => {
    if (!can("notification.template.manage")) return;
    if (
      !form.nameAr.trim() ||
      !form.nameEn.trim() ||
      !form.bodyAr.trim() ||
      !form.bodyEn.trim()
    ) {
      alert(t.builder.required);
      return;
    }

    const token = getToken();

    if (!token) return;

    if (saving) return;
    setSaving(true);

    try {
      const payload = getPayload();

      const res = editingId
        ? await updateNotificationTemplateAPI(token, editingId, payload)
        : await createNotificationTemplateAPI(token, payload);

      if (!res?.success) {
        reportFailedBody(res);
        return;
      }

      setBuilderCat(form.category);
      resetForm(form.category);
      toastSaved(t.builder.saved);
      await onTemplatesChange?.();
    } catch (error) {
      // Network/refresh failures used to become unhandled rejections.
      console.error(error);
      reportApiError(error, { always: true, key: "template-save-failed" });
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!can("notification.template.manage")) return;
    if (!templateId || deleteState.loading) return;

    const token = getToken();

    if (!token) return;

    setDeleteState({ loading: true, error: "" });
    try {
      const res = await deleteNotificationTemplateAPI(token, templateId);

      if (!res?.success) {
        throw new Error(res?.message || "");
      }

      setDeleteTarget(null);
      setDeleteState({ loading: false, error: "" });
      resetForm(builderCat);
      await onTemplatesChange?.();
    } catch (error) {
      console.error(error);
      // Shown inside the still-open confirmation dialog.
      setDeleteState({
        loading: false,
        error:
          error?.message ||
          (ar ? "تعذّر حذف القالب" : "The template could not be deleted"),
      });
    }
  };

  const previewAr = form.bodyAr
    ? `${form.bodyAr}${form.needsDetail ? form.placeholderAr || "[التفاصيل]" : ""}`
    : t.builder.previewAr;

  const previewEn = form.bodyEn
    ? `${form.bodyEn}${form.needsDetail ? form.placeholderEn || "[Details]" : ""}`
    : t.builder.previewEn;

  return (
    <div className="lud-notif-builder-view">
      <Card className="lud-notif-builder-intro">
        <strong>{t.builder.title}</strong>
        <p>{t.builder.intro}</p>
      </Card>

      <div className="lud-notif-builder-grid">
        <Card className="lud-notif-builder-side">
          <div className="lud-notif-section-label">{t.builder.section}</div>

          <div className="lud-notif-cat-buttons">
            {BUILDER_CATS.map((category) => (
              <button
                key={category}
                type="button"
                className={builderCat === category ? "is-active" : ""}
                onClick={() => handleCategory(category)}
              >
                {t.categories[category]}
              </button>
            ))}
          </div>

          <div className="lud-notif-section-label">{t.builder.templates}</div>

          <div className="lud-notif-template-list">
            {selectedList.length ? (
              selectedList.map((template) => {
                const templateId = getTemplateId(template);

                return (
                  <button
                    key={templateId}
                    type="button"
                    className={`lud-notif-template-item ${
                      editingId === templateId ? "is-selected" : ""
                    }`}
                    onClick={() => editTemplate(template)}
                  >
                    <strong>{getLocalized(template, "nameAr", "nameEn", lang)}</strong>
                    <span>
                      {getLocalized(template, "bodyAr", "bodyEn", lang).slice(0, 60)}
                      ...
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="lud-notif-muted-text">{t.builder.noTemplates}</div>
            )}
          </div>

          <Button
            variant="primary"
            appearance="soft"
            icon={<Plus size={15} />}
            onClick={() => resetForm(builderCat)}
          >
            {t.builder.newTemplate}
          </Button>
        </Card>

        <div className="lud-notif-right-col">
          <Card className="lud-notif-preview-card">
            <div className="lud-notif-preview-head">
              <strong>{editingId ? t.builder.editTemplate : t.builder.newTemplate}</strong>
              <span>{currentCategoryLabel}</span>
            </div>

            <div className="lud-notif-preview-body">
              <div className="lud-builder-select">
                <Select
                  label={t.builder.section}
                  value={form.category}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      category: event.target.value,
                    }))
                  }
                  options={BUILDER_CATS.map((category) => ({
                    value: category,
                    label: t.categories[category],
                  }))}
                />
              </div>

              <div className="lud-notif-lang-grid">
                <div className="lud-notif-form-group">
                  <label>{t.builder.templateNameAr}</label>
                  <Input
                    value={form.nameAr}
                    placeholder={t.builder.nameArPlaceholder}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, nameAr: event.target.value }))
                    }
                  />
                </div>

                <div className="lud-notif-form-group" dir="ltr">
                  <label>{t.builder.templateNameEn}</label>
                  <Input
                    value={form.nameEn}
                    placeholder={t.builder.nameEnPlaceholder}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, nameEn: event.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="lud-notif-lang-grid">
                <div className="lud-notif-form-group">
                  <label>{t.builder.bodyAr}</label>
                  <textarea
                    value={form.bodyAr}
                    placeholder={t.builder.bodyArPlaceholder}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, bodyAr: event.target.value }))
                    }
                  />
                </div>

                <div className="lud-notif-form-group" dir="ltr">
                  <label>{t.builder.bodyEn}</label>
                  <textarea
                    value={form.bodyEn}
                    placeholder={t.builder.bodyEnPlaceholder}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, bodyEn: event.target.value }))
                    }
                  />
                </div>
              </div>

              <label className="lud-notif-check-row">
                <input
                  type="checkbox"
                  checked={form.needsDetail}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      needsDetail: event.target.checked,
                    }))
                  }
                />
                <span>{t.builder.needsDetail}</span>
              </label>

              {form.needsDetail ? (
                <>
                  <div className="lud-notif-lang-grid">
                    <div className="lud-notif-form-group">
                      <label>{t.builder.placeholderAr}</label>
                      <Input
                        value={form.placeholderAr}
                        placeholder={t.builder.placeholderArExample}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            placeholderAr: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="lud-notif-form-group" dir="ltr">
                      <label>{t.builder.placeholderEn}</label>
                      <Input
                        value={form.placeholderEn}
                        placeholder={t.builder.placeholderEnExample}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            placeholderEn: event.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="lud-notif-form-group">
                    <label>{t.builder.hint}</label>
                    <Input
                      value={form.detailHint}
                      placeholder={t.builder.hintPlaceholder}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          detailHint: event.target.value,
                        }))
                      }
                    />
                  </div>
                </>
              ) : null}

              <div className="lud-notif-section-label">{t.builder.preview}</div>
              <span className={`lud-notif-badge lud-notif-badge--${form.category}`}>
                {currentCategoryLabel}
              </span>

              <div className="lud-notif-preview-label">{t.send.arabic}</div>
              <div className={`lud-notif-preview-text ${!form.bodyAr ? "is-muted" : ""}`}>
                {previewAr}
              </div>

              <div className="lud-notif-preview-label" dir="ltr">
                {t.send.english}
              </div>
              <div
                className={`lud-notif-preview-text ${!form.bodyEn ? "is-muted" : ""}`}
                dir="ltr"
              >
                {previewEn}
              </div>
            </div>
          </Card>

          <div className="lud-notif-builder-actions">
            <Button
              variant="primary"
              appearance="filled"
              icon={<Save size={16} />}
              onClick={saveTemplate}
              disabled={saving}
            >
              {t.builder.save}
            </Button>

            {editingId ? (
              <>
                <Button
                  variant="danger"
                  appearance="filled"
                  icon={<Trash2 size={15} />}
                  onClick={() => setDeleteTarget(editingId)}
                  disabled={saving}
                >
                  {t.builder.delete}
                </Button>

                <Button
                  variant="neutral"
                  appearance="soft"
                  icon={<CircleX size={15} />}
                  onClick={() => resetForm(builderCat)}
                  disabled={saving}
                >
                  {t.builder.cancel}
                </Button>
              </>
            ) : null}
          </div>

          <p className="lud-notif-hint-text">{t.builder.note}</p>
        </div>
      </div>

      {deleteTarget ? (
        <DeleteModal
          id={deleteTarget}
          loading={deleteState.loading}
          error={deleteState.error}
          handleDelete={deleteTemplate}
          cancel={() => {
            setDeleteTarget(null);
            setDeleteState({ loading: false, error: "" });
          }}
        />
      ) : null}
    </div>
  );
}
